import React, { useState } from 'react';
import { API_BASE_URL, publicApiHeaders } from '../config/apiConfig';
import { resolveSiteImage, useSiteContent } from '../context/SiteContentContext';

function Home() {
  const { images, sections, methodSteps, recommendations, services, testimonials } = useSiteContent();
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    mensaje: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.name]: event.target.value
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/contact`, {
        method: 'POST',
        headers: publicApiHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(formData)
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data.error || 'No se pudo enviar el mensaje. Inténtalo nuevamente.');
      }

      setFormData({ nombre: '', email: '', mensaje: '' });
      setSubmitStatus({
        type: 'success',
        message: 'Mensaje recibido. Gracias por contactarme; te responderé a la brevedad.'
      });
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: error.message || 'No se pudo enviar el mensaje. Inténtalo nuevamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return (
    <main className="one-page editorial-site">
      <section id="inicio" className="editorial-hero one-page-section">
        <div className="editorial-hero-copy">
          <p className="eyebrow">{sections.hero.eyebrow}</p>
          <h1>{sections.hero.title}</h1>
          <p>{sections.hero.description}</p>
          <div className="hero-actions">
            <a href="#servicios" className="button button-primary">{sections.hero.primaryButton}</a>
            <a href="#metodo" className="button button-secondary">{sections.hero.secondaryButton}</a>
          </div>
        </div>

        <figure className="editorial-hero-media">
          <img src={resolveSiteImage(images.hero.src)} alt={images.hero.alt} />
        </figure>
      </section>

      <section className="lead-feature one-page-section">
        <div>
          <p className="eyebrow">{sections.lead.eyebrow}</p>
          <h2>{sections.lead.title}</h2>
        </div>
        <p>
          {sections.lead.description}
        </p>
      </section>

      <section id="sobre-mi" className="story-section one-page-section">
        <figure className="story-image tall">
          <img src={resolveSiteImage(images.about.src)} alt={images.about.alt} />
        </figure>

        <article className="story-copy">
          <p className="eyebrow">{sections.about.eyebrow}</p>
          <h2>{sections.about.title}</h2>
          {sections.about.paragraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}
          <a href="#contacto" className="text-link">{sections.about.button}</a>
        </article>
      </section>

      <section id="metodo" className="method-section one-page-section">
        <div className="method-intro">
          <p className="eyebrow">{sections.method.eyebrow}</p>
          <h2>{sections.method.title}</h2>
          <p>{sections.method.description}</p>
        </div>

        <div className="method-grid">
          {methodSteps.map((step, index) => (
            <article className="method-card" key={step.title}>
              <span>{String(index + 1).padStart(2, '0')}</span>
              <h3>{step.title}</h3>
              <p>{step.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section id="servicios" className="services-showcase one-page-section">
        <header className="section-header centered-heading">
          <p className="eyebrow">{sections.services.eyebrow}</p>
          <h2>{sections.services.title}</h2>
        </header>

        <div className="service-list">
          {services.map((service, index) => (
            <article className="service-row" key={service.title}>
              <div className="service-number">{String(index + 1).padStart(2, '0')}</div>
              <div>
                <p className="service-subtitle">{service.subtitle}</p>
                <h3>{service.title}</h3>
                <p>{service.description}</p>
              </div>
              <ul>
                {service.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="full-bleed-cta one-page-section">
        <img src={resolveSiteImage(images.method.src)} alt={images.method.alt} />
        <div>
          <p className="eyebrow">{sections.callToAction.eyebrow}</p>
          <h2>{sections.callToAction.title}</h2>
          <a href="#contacto" className="button button-primary">{sections.callToAction.button}</a>
        </div>
      </section>

      <section id="recomendaciones" className="page-shell one-page-section">
        <div className="narrow-container">
          <header className="section-header">
            <p className="eyebrow">{sections.recommendations.eyebrow}</p>
            <h2>{sections.recommendations.title}</h2>
            <p className="section-lede">{sections.recommendations.description}</p>
          </header>

          <div className="recommendation-stack">
            {recommendations.map((section) => (
              <article className="recommendation-card" key={section.title}>
                <h3>{section.title}</h3>
                <p>{section.copy}</p>
                <ul className="feature-list">
                  {section.items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
                {section.href && (
                  <a className="text-link" href={section.href} target="_blank" rel="noreferrer">
                    {section.cta}
                  </a>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="testimonios" className="testimonial-band one-page-section">
        <div className="container">
          <header className="section-header centered-heading">
            <p className="eyebrow">{sections.testimonials.eyebrow}</p>
            <h2>{sections.testimonials.title}</h2>
          </header>

          <div className="testimonials-grid">
            {testimonials.map((testimonial) => (
              <article className="modern-card" key={testimonial.name}>
                <div className="quote-mark">“</div>
                <p>{testimonial.quote}</p>
                <h4>{testimonial.name}</h4>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="contacto" className="contact-split one-page-section">
        <figure>
          <img src={resolveSiteImage(images.contact.src)} alt={images.contact.alt} />
        </figure>
        <div className="contact-card">
          <p className="eyebrow">{sections.contact.eyebrow}</p>
          <h2>{sections.contact.title}</h2>
          <p className="section-lede">{sections.contact.description}</p>

          <form onSubmit={handleSubmit} className="form-grid">
            <div className="form-field">
              <label htmlFor="nombre">Nombre</label>
              <input id="nombre" type="text" name="nombre" value={formData.nombre} onChange={handleChange} required />
            </div>

            <div className="form-field">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" name="email" value={formData.email} onChange={handleChange} required />
            </div>

            <div className="form-field">
              <label htmlFor="mensaje">Mensaje</label>
              <textarea id="mensaje" name="mensaje" value={formData.mensaje} onChange={handleChange} required />
            </div>

            <button type="submit" className="button button-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Enviando…' : 'Enviar mensaje'}
            </button>

            {submitStatus.message && (
              <p className={`form-status ${submitStatus.type}`} role="status" aria-live="polite">
                {submitStatus.message}
              </p>
            )}
          </form>
        </div>
      </section>
    </main>
  );
}

export default Home;
